import type { LiveQuoteRequest, LiveTradeCreateRequest } from 'apiConnector/secondary-market/live/models';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import { liveReferentialSelectors } from 'state/secondaryMarket/live/referential/liveReferential.selector';
import { liveTradeActions } from 'state/secondaryMarket/live/trade/trade.action';
import { fromSecondaryQuotesWorkspace } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import { getProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import {
  workspaceTabFrozen,
  workspaceTabRemoved,
  workspaceTabSetBusy,
  workspaceTabUnactivated,
  workspaceTabUnfrozen,
} from 'state/workspaces/workspace.actions';
import { getTabWorkspaceType } from 'state/workspaces/workspaces/workspaces.selector';

import {
  secondaryMarketLiveQuoteCancelRequested,
  secondaryMarketLiveQuoteCleared,
  secondaryMarketLiveQuoteRequested,
} from './liveQuote.action';
import { getLiveQuotePriceType, getLiveQuoteQuoteId } from './liveQuote.selector';

function getQuoteRequest(state: AppState, tabId: string): LiveQuoteRequest {
  const productKey = fromSecondaryQuotesWorkspace.getProductKey(state, tabId);
  const fileCode = liveReferentialSelectors.getFileCode(state, tabId);
  const client = liveSelectors.getFormClient(state, tabId);
  const clientType = liveSelectors.getFormClientType(state, tabId);
  const salesId = liveSelectors.getFormSalesId(state, tabId);
  const custodianBdrId = liveSelectors.getFormCustodianBdrId(state, tabId);
  const settlementDate = liveSelectors.getFormSettlementDate(state, tabId);
  const settlement = liveSelectors.getFormSettlement(state, tabId);
  const way = liveSelectors.getFormTradeWay(state, tabId);
  const cashAmount = liveFormInputSelectors.getFormCashAmount(state, tabId);
  const quantity = liveFormInputSelectors.getFormQuantity(state, tabId);
  const tradeSizeMode = liveFormInputSelectors.getFormTradeSizeInputMode(state, tabId);
  const status = liveSelectors.getStatus(state, tabId);
  const isRejected =
    status === 'QUOTE_FAILED' ||
    status === 'QUOTE_REJECTED' ||
    status === 'TRADE_FAILED' ||
    status === 'TRADE_REJECTED';
  const shouldResetSidePanel = isRejected;
  const previousQuoteId = getLiveQuoteQuoteId(state, tabId);

  return {
    productKey,
    clientType: clientType,
    clientBdrId: client?.id,
    clientEliotCode: client?.eliotCode,
    fileCode,
    cashAmount: tradeSizeMode === 'amount' ? cashAmount : undefined,
    quantity: tradeSizeMode === 'quantity' ? quantity : undefined,
    salesId,
    custodianBdrId,
    settlementDate,
    settlementMarketType: settlement?.marketType,
    settlementType: settlement?.settlementType,
    way,
    hasUpfrontFee: true,
    shouldResetSidePanel,
    previousQuoteId,
  };
}

function getTradeRequest(state: AppState, tabId: string): LiveTradeCreateRequest {
  const comment = liveFormInputSelectors.getFormComment(state, tabId);
  const quoteId = getLiveQuoteQuoteId(state, tabId);
  const salesMarginAdjustment = liveFormInputSelectors.getFormInputCustomizedXMarginAdjustmentFormatted(state, tabId);
  const tradingPriceAdjustment = liveFormInputSelectors.getFormInputCustomizedTradingPriceAdjustmentFormatted(
    state,
    tabId,
  );
  const upfront = liveFormInputSelectors.getFormInputCustomizedUpfrontFormatted(state, tabId);
  const eligibleTraceReporting = liveReferentialSelectors.getEligibleTraceReporting(state, tabId);
  const performTraceReporting = eligibleTraceReporting
    ? liveFormInputSelectors.getFormInputTraceReporting(state, tabId)
    : undefined;
  const priceType = getLiveQuotePriceType(state, tabId) ?? liveReferentialSelectors.getPriceType(state, tabId);

  return {
    comment,
    quoteId,
    salesMarginAdjustment,
    tradingPriceAdjustment,
    performTraceReporting,
    priceType,
    upfront,
  };
}

export function secondaryMarketLiveQuoteRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const request = getQuoteRequest(state, tabId);
    const workspaceType = getTabWorkspaceType(state, tabId)!;
    if (request.shouldResetSidePanel && request.previousQuoteId) {
      dispatch(secondaryMarketLiveQuoteCancelRequested(tabId, request.previousQuoteId, request.productKey));
    }
    dispatch(secondaryMarketLiveQuoteRequested(tabId, workspaceType as SecondaryWorkspaceType, request));
    dispatch(workspaceTabFrozen(tabId, workspaceType!, true));
  };
}

export function secondaryMarketLiveTradeRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const request = getTradeRequest(state, tabId);
    const workspaceType = getTabWorkspaceType(state, tabId)!;
    dispatch(liveTradeActions.secondaryMarketTradeRequested(tabId, workspaceType as SecondaryWorkspaceType, request));
    dispatch(workspaceTabSetBusy(tabId, workspaceType!, true));
  };
}

export function secondaryMarketLiveQuoteCancelRequestedThunk(
  tabId: string,
  closeTab?: boolean,
  shouldSendCancellation?: boolean,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const quoteId = getLiveQuoteQuoteId(state, tabId)!;
    const workspaceType = getTabWorkspaceType(state, tabId)!;
    const productKey = getProductKey(state, tabId);
    dispatch(secondaryMarketLiveQuoteCancelRequested(tabId, quoteId, productKey, shouldSendCancellation));
    dispatch(workspaceTabUnfrozen(tabId, workspaceType!, false));
    if (closeTab === true) {
      dispatch(workspaceTabRemoved(tabId, 'secondaryMarketQuotes'));
      dispatch(workspaceTabUnactivated('secondaryMarketQuotes'));
    }
  };
}

export function secondaryMarketLiveRepriceQuoteThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const request = getQuoteRequest(state, tabId);
    const workspaceType = getTabWorkspaceType(state, tabId)!;
    dispatch(secondaryMarketLiveQuoteCleared(tabId));
    dispatch(secondaryMarketLiveQuoteRequested(tabId, workspaceType as SecondaryWorkspaceType, request, true));
  };
}
